import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function Athlete() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_MINF_LATEST_BAC_BASE}/api/player`);
        const data = response.data;
        // Only include players with status 'success'
        const transformed = data.filter(player => player.status === "success").map((player, idx) => {
          const medals = { gold: 0, silver: 0, bronze: 0 };
          player.achievements.forEach(a => {
            if (a.medal === "Gold") medals.gold += 1;
            if (a.medal === "Silver") medals.silver += 1;
            if (a.medal === "Bronze") medals.bronze += 1;
          });
          return {
            id: idx + 1,
            playerId: player.playerId,
            athleteImage: player.photo,
            athleteName: player.name,
            state: player.state,
            medals,
            status: player.status, // keep for future use if needed
          };
        });
        setRankings(transformed);
      } catch (err) {
        console.error("Failed to fetch players", err);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  // Filter rankings based on search term
  const filteredRankings = rankings.filter(item =>
    item.athleteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.state.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredRankings.length / itemsPerPage);
  const paginatedRankings = filteredRankings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleAthleteClick = (athleteId) => {
    navigate(`/players/profile`, {
      state: { athlete: athleteId }
    });
  };

  // SkeletonRow for desktop
  function SkeletonRow() {
    return (
      <tr className="animate-pulse">
        <td className="px-6 py-3">
          <div className="h-4 w-8 bg-gray-200 rounded" />
        </td>
        <td className="px-6 py-3">
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 bg-gray-200 rounded-full" />
            <div className="flex flex-col space-y-2">
              <div className="h-4 w-24 bg-gray-200 rounded" />
              <div className="h-3 w-16 bg-gray-100 rounded" />
            </div>
          </div>
        </td>
        <td className="px-6 py-3">
          <div className="h-4 w-16 bg-gray-200 rounded" />
        </td>
        <td className="px-6 py-3">
          <div className="flex space-x-3">
            <div className="h-6 w-8 bg-gray-200 rounded-full" />
            <div className="h-6 w-8 bg-gray-200 rounded-full" />
            <div className="h-6 w-8 bg-gray-200 rounded-full" />
          </div>
        </td>
      </tr>
    );
  }

  // SkeletonCard for mobile
  function SkeletonCard() {
    return (
      <div className="p-4 border-b border-gray-200 last:border-b-0 animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 bg-gray-200 rounded-full" />
            <div>
              <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-16 bg-gray-100 rounded" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="h-6 w-16 bg-gray-200 rounded-full" />
          <div className="flex space-x-2">
            <div className="h-6 w-8 bg-gray-200 rounded-full" />
            <div className="h-6 w-8 bg-gray-200 rounded-full" />
            <div className="h-6 w-8 bg-gray-200 rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <section className="min-h-screen ">
        <div
          className="mx-5 mb-12 rounded-lg relative overflow-hidden "
          style={{backgroundImage: "url('https://i.pinimg.com/736x/96/5c/8f/965c8f6a1faedc80d75e670288001547.jpg')", backgroundSize: "cover", backgroundPosition: "center"}}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent" />
          <div className="relative flex h-[350px] justify-center rounded-lg items-center">
            <div className="text-center">
              <h1 className="px-8 text-6xl md:text-7xl text-center text-white tracking-widest mt-[80px] interf pb-2 font-bold animate-fade-in">
                Rankings
              </h1>
              <p className="text-xl text-gray-100 mt-4 animate-fade-in-delay">Official Athlete Rankings</p>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 pb-12">
          <div className="mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <div className="w-full px-4 py-3 rounded-lg bg-gray-100 border-2 border-gray-200 animate-pulse" />
            </div>
          </div>
          <div className="rounded-3xl shadow-xl overflow-hidden border border-gray-200 bg-white">
            {/* Desktop Table Skeleton */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Athlete</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">State</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Medals</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[...Array(10)].map((_, i) => <SkeletonRow key={i} />)}
                </tbody>
              </table>
            </div>
            {/* Mobile Card Skeleton */}
            <div className="md:hidden">
              {[...Array(10)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </div>
          {/* Pagination Skeleton */}
          <div className="mt-6 flex justify-center items-center space-x-4">
            <div className="p-2 rounded-lg border border-gray-200 bg-gray-100 w-10 h-10 animate-pulse" />
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg">
              <div className="h-4 w-8 bg-gray-200 rounded animate-pulse" />
              <span className="text-gray-700 font-medium">/</span>
              <div className="h-4 w-8 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="p-2 rounded-lg border border-gray-200 bg-gray-100 w-10 h-10 animate-pulse" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen ">
      <div
        className="mx-5 mb-12 rounded-lg relative overflow-hidden "
        style={{backgroundImage: "url('https://i.pinimg.com/736x/96/5c/8f/965c8f6a1faedc80d75e670288001547.jpg')", backgroundSize: "cover", backgroundPosition: "center"}}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent" />
        <div className="relative flex h-[350px] justify-center rounded-lg items-center">
          <div className="text-center">
            <h1 className="px-8 text-6xl md:text-7xl text-center text-white tracking-widest mt-[80px] interf pb-2 font-bold animate-fade-in">
              Athletes
            </h1>
            <p className="text-xl text-gray-100 mt-4 animate-fade-in-delay">Official Athlete of WBCAMTI</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pb-12">
        {/* Search and Filter Section */}
        <div className="mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96">
            <input
              type="text"
              placeholder="Search athletes or states..."
              className="w-full px-4 py-3 rounded-lg bg-transparent border-2 outline-none border-gray-500 focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-300"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg
              className="absolute right-3 top-3.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        <div className="rounded-3xl shadow-xl overflow-hidden border border-gray-200 bg-white">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Athlete</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">State</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Medals</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedRankings.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-xl text-gray-400 font-semibold">
                      Coming Soon
                    </td>
                  </tr>
                ) : (
                  paginatedRankings.map((item) => (
                    <tr 
                      key={item.id} 
                      className="group hover:bg-gray-50 transition-all duration-300 ease-in-out cursor-pointer"
                      onClick={() => handleAthleteClick(item.playerId)}
                    >
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`text-md font-bold transition-all duration-300 group-hover:scale-110 ${
                            'text-gray-500'
                          }`}>
                            {item.id}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0 h-12 w-12 relative group-hover:scale-105 transition-transform duration-300">
                            <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full p-[2px] group-hover:from-gray-200 group-hover:to-gray-300 transition-all duration-300">
                              <img 
                                src={item.athleteImage} 
                                alt={`${item.athleteName}`} 
                                className="h-full w-full rounded-full object-cover border border-white"
                              />
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-base font-semibold text-gray-900 group-hover:text-gray-800 transition-colors duration-300">{item.athleteName}</span>
                            <span className="text-xs text-gray-500 group-hover:text-gray-600 transition-colors duration-300">Professional Athlete</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 text-xs font-medium border border-gray-200 group-hover:from-gray-200 group-hover:to-gray-300 group-hover:border-gray-300 transition-all duration-300">
                            {item.state}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex space-x-3">
                          <span className="flex items-center bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1 rounded-full transition-all duration-300">
                            <span className="mr-1.5 text-sm">🥇</span>
                            <span className="font-semibold text-sm text-gray-700">{item.medals.gold}</span>
                          </span>
                          <span className="flex items-center bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1 rounded-full transition-all duration-300">
                            <span className="mr-1.5 text-sm">🥈</span>
                            <span className="font-semibold text-sm text-gray-600">{item.medals.silver}</span>
                          </span>
                          <span className="flex items-center bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1 rounded-full transition-all duration-300">
                            <span className="mr-1.5 text-sm">🥉</span>
                            <span className="font-semibold text-sm text-gray-500">{item.medals.bronze}</span>
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden">
            {paginatedRankings.length === 0 ? (
              <div className="text-center py-12 text-xl text-gray-400 font-semibold">Coming Soon</div>
            ) : (
              paginatedRankings.map((item) => (
                <div 
                  key={item.id} 
                  className="p-4 border-b border-gray-200 last:border-b-0 cursor-pointer"
                  onClick={() => handleAthleteClick(item.id)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full p-[2px]">
                          <img 
                            src={item.athleteImage} 
                            alt={`${item.athleteName}`} 
                            className="h-full w-full rounded-full object-cover border border-white"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-gray-500">#{item.id}</span>
                          <span className="text-base font-semibold text-gray-900">{item.athleteName}</span>
                        </div>
                        <span className="text-xs text-gray-500">Professional Athlete</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 text-xs font-medium border border-gray-200">
                      {item.state}
                    </span>
                    <div className="flex space-x-2">
                      <span className="flex items-center bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1 rounded-full">
                        <span className="mr-1.5 text-sm">🥇</span>
                        <span className="font-semibold text-sm text-gray-700">{item.medals.gold}</span>
                      </span>
                      <span className="flex items-center bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1 rounded-full">
                        <span className="mr-1.5 text-sm">🥈</span>
                        <span className="font-semibold text-sm text-gray-600">{item.medals.silver}</span>
                      </span>
                      <span className="flex items-center bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1 rounded-full">
                        <span className="mr-1.5 text-sm">🥉</span>
                        <span className="font-semibold text-sm text-gray-500">{item.medals.bronze}</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-6 flex justify-center items-center space-x-4">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-gray-200 bg-gray-900 text-white hover:bg-gray-950 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg">
            <span className=" text-gray-700  py-1 rounded-md font-semibold">{currentPage}</span>
            <span className="text-gray-700 font-medium">/</span>
            <span className=" text-gray-700 py-1 rounded-md font-semibold">{totalPages}</span>
          </div>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-gray-200 bg-gray-900 text-white hover:bg-gray-950 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            aria-label="Next page"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
}
